import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PunishmentStep } from '../types';
import { rulesApi } from '../api/rules';

export const useRules = (guildId: string | null) => {
  return useQuery({
    queryKey: ['rules', guildId],
    queryFn: () => rulesApi.getRules(guildId!),
    enabled: !!guildId,
  });
};

export const useCreateRule = (guildId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; content: string; punishments: PunishmentStep[] }) =>
      rulesApi.createRule(guildId!, data),
    onMutate: async (newRule) => {
      // 진행 중인 쿼리 취소
      await queryClient.cancelQueries({ queryKey: ['rules', guildId] });

      // 이전 데이터 스냅샷
      const previousRules = queryClient.getQueryData(['rules', guildId]);

      // 낙관적 업데이트
      queryClient.setQueryData(['rules', guildId], (old: any) => {
        if (!old) return old;
        return [
          ...old,
          {
            _id: 'temp-' + Date.now(),
            guildId: guildId!,
            name: newRule.name,
            content: newRule.content,
            punishments: newRule.punishments,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: 'current-user',
          },
        ];
      });

      return { previousRules };
    },
    onError: (_err, _newRule, context) => {
      // 오류 발생 시 롤백
      if (context?.previousRules) {
        queryClient.setQueryData(['rules', guildId], context.previousRules);
      }
    },
    onSettled: () => {
      // 성공/실패 관계없이 최종적으로 리프레시
      queryClient.invalidateQueries({ queryKey: ['rules', guildId] });
    },
  });
};

export const useUpdateRule = (guildId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { ruleName: string; content: string; punishments: PunishmentStep[] }) =>
      rulesApi.updateRule(guildId!, data.ruleName, {
        content: data.content,
        punishments: data.punishments,
      }),
    onMutate: async (updatedRule) => {
      await queryClient.cancelQueries({ queryKey: ['rules', guildId] });

      const previousRules = queryClient.getQueryData(['rules', guildId]);

      queryClient.setQueryData(['rules', guildId], (old: any) => {
        if (!old) return old;
        return old.map((rule: any) =>
          rule.name === updatedRule.ruleName
            ? {
                ...rule,
                content: updatedRule.content,
                punishments: updatedRule.punishments,
                updatedAt: new Date().toISOString(),
              }
            : rule
        );
      });

      return { previousRules };
    },
    onError: (_err, _updatedRule, context) => {
      if (context?.previousRules) {
        queryClient.setQueryData(['rules', guildId], context.previousRules);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['rules', guildId] });
    },
  });
};

export const useDeleteRule = (guildId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ruleName: string) => rulesApi.deleteRule(guildId!, ruleName),
    onMutate: async (ruleName) => {
      await queryClient.cancelQueries({ queryKey: ['rules', guildId] });

      const previousRules = queryClient.getQueryData(['rules', guildId]);

      queryClient.setQueryData(['rules', guildId], (old: any) => {
        if (!old) return old;
        return old.filter((rule: any) => rule.name !== ruleName);
      });

      return { previousRules };
    },
    onError: (_err, _ruleName, context) => {
      if (context?.previousRules) {
        queryClient.setQueryData(['rules', guildId], context.previousRules);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['rules', guildId] });
    },
  });
};
